<template>
    <div id="app">
        <v-app>
            <router-view :key="$i18n.locale"/>
        </v-app>
    </div>
</template>

<script>
import api from "@/api";
import { initCommonMetatag_Vmids, interpolateCommonMetatag_Value, isCommonMetatag_Printable } from "@/store/metatags";

////////////////////////////////////////////////////////////////////////////////
// 공통 메타태그 → vue-meta (SPA 안에서 페이지를 옮길 때 <head> 의 태그를 맞춘다)
// - 원본(mevn3 router/index.js)처럼 title·meta·link·script·style·noscript 만 넘긴다.
//   그 밖의 종류는 서버가 index.html 에 넣을 때만 쓰이고 SPA 이동 때는 바뀌지 않는다 (vue-meta 는 그 종류를 만들지도 지우지도 않는다)
// - 응답의 tags 는 변수 치환 전이다. variables_object 로 ${이름} 을 바꾼다 (interpolateCommonMetatag_Value. 서버와 같은 규칙)
// - vmid 는 서버가 넣은 태그의 data-vmid 와 같은 규칙(initCommonMetatag_Vmids)이라 vue-meta 가 그 태그를 자기 것으로 본다
// - vue-meta 는 mount 직후 갱신에서 [data-vmid] 태그 가운데 넘겨받지 않은 것을 지운다.
//   그래서 처음에는 문서에 있는 data-vmid 태그와 제목을 읽어 그대로 넘긴다. 첫 응답 전·첫 조회 실패 때 서버가 넣은 태그가 남는다
// - vmid 있는 태그는 once 로 넘겨 data-vue-meta 속성을 붙이지 않는다. 그래야 서버가 넣은 태그와 isEqualNode 로 같아서
//   지웠다 다시 넣지 않고(script 가 다시 실행되지 않는다), SPA 이동 때 같은 vmid·같은 내용의 script 도 그대로 둔다
// - 실패하면 지금 상태를 둔다. 등록 안 된 주소로 옮기면 index.html 상태로 돌아온다:
//   제목은 처음 문서의 title(서버가 title 을 넣어 보냈으면 원래 제목을 알 수 없어 빈 문자열), meta 는 처음 문서의 정적 meta(name/property)
////////////////////////////////////////////////////////////////////////////////
// vue-meta 에 넘기는 종류 (원본 router/index.js 와 같다). title 은 따로 다룬다
const VUE_META_TYPES = ["meta", "link", "script", "style", "noscript"];
// 본문을 넣을 수 있는 종류 (vue-meta.esm.js tagsWithInnerContent). meta·link 는 빈 요소라 서버도 본문을 쓰지 않는다
const VUE_META_BODY_TYPES = ["script", "style", "noscript"];

// vue-meta 2.4 가 값을 무시하고 이름만 쓰는 속성 (node_modules/vue-meta/dist/vue-meta.esm.js booleanHtmlAttributes)
const BOOLEAN_ATTRS = ["allowfullscreen", "amp", "amp-boilerplate", "async", "autofocus", "autoplay", "checked", "compact", "controls", "declare", "default", "defaultchecked", "defaultmuted", "defaultselected", "defer", "disabled", "enabled", "formnovalidate", "hidden", "indeterminate", "inert", "ismap", "itemscope", "loop", "multiple", "muted", "nohref", "noresize", "noshade", "novalidate", "nowrap", "open", "pauseonexit", "readonly", "required", "reversed", "scoped", "seamless", "selected", "sortable", "truespeed", "typemustmatch", "visible"];
// vue-meta 가 속성이 아니라 자기 설정으로 읽는 이름. 속성으로 넘길 수 없어 뺀다 (innerHTML·cssText 는 소문자로 바꾸면 보통 속성이 된다)
const VUE_META_RESERVED = ["vmid", "once", "skip", "template", "body", "pbody", "callback", "json", "__proto__"];

/** 태그 data([{ key, value }]) → vue-meta 태그 객체. 속성 이름은 HTML 처럼 소문자, 같은 이름은 앞의 것, 값이 빈 속성은 이름만 */
function toVueMetaItem(type, data, vmid) {
    const item = {};
    for (const { key, value } of data) {
        if (key == "slot") {
            if (VUE_META_BODY_TYPES.includes(type)) item.innerHTML = item.innerHTML === undefined ? String(value ?? "") : `${item.innerHTML}\n${value ?? ""}`;
            continue;
        }
        const name = String(key).toLowerCase();
        if (VUE_META_RESERVED.includes(name) || Object.prototype.hasOwnProperty.call(item, name)) continue;
        item[name] = value === "" || value == null ? (BOOLEAN_ATTRS.includes(name) ? true : "") : String(value);
    }
    if (vmid) {
        item.vmid = vmid;
        item.once = true;
    }
    return item;
}

const initMetaInfo = (title) => ({ title, meta: [], link: [], script: [], style: [], noscript: [] });

function toMetaInfo(tags = [], defaults = [], title = "") {
    const metaInfo = initMetaInfo(title);
    const indexes = {};
    const items = [...defaults, ...(tags || [])].filter(isCommonMetatag_Printable);
    // vmid 순번은 서버처럼 vue-meta 가 못 다루는 종류까지 합친 목록에서 센다
    const vmids = initCommonMetatag_Vmids(items);
    items.forEach((tag, index) => {
        const type = String(tag.type).toLowerCase();
        if (type == "title") {
            metaInfo.title = tag.data
                .filter(({ key }) => key == "slot")
                .map(({ value }) => String(value ?? ""))
                .join("\n");
            return;
        }
        if (!VUE_META_TYPES.includes(type)) return;
        const vmid = vmids[index];
        const item = toVueMetaItem(type, tag.data, vmid);
        // 같은 vmid 는 뒤의 것(주소에 등록된 태그)으로 바꾼다
        if (vmid && indexes[vmid] !== undefined) metaInfo[type].splice(indexes[vmid], 1, item);
        else {
            if (vmid) indexes[vmid] = metaInfo[type].length;
            metaInfo[type].push(item);
        }
    });
    return metaInfo;
}

/** 처음 열린 문서의 제목과 서버가 넣은 태그(data-vmid). vue-meta 의 첫 갱신이 이 태그를 지우지 않게 그대로 넘긴다 */
function readServerMetaInfo() {
    const metaInfo = initMetaInfo(document.title);
    document.head.querySelectorAll(VUE_META_TYPES.map((type) => `${type}[data-vmid]`).join(", ")).forEach((element) => {
        const type = element.tagName.toLowerCase();
        const data = [...element.attributes].filter(({ name }) => name != "data-vmid").map(({ name, value }) => ({ key: name, value }));
        // vue-meta 는 이름만 쓰는 속성에 빈 값을 넣는다. 서버 태그도 빈 값으로 맞춰 둬야 같은 태그로 본다 (값을 바꿔도 script 는 다시 실행되지 않는다)
        data.forEach(({ key }) => BOOLEAN_ATTRS.includes(key) && element.setAttribute(key, ""));
        if (VUE_META_BODY_TYPES.includes(type) && element.innerHTML) data.push({ key: "slot", value: element.innerHTML });
        metaInfo[type].push(toVueMetaItem(type, data, element.getAttribute("data-vmid")));
    });
    return metaInfo;
}

/** 처음 열린 문서의 제목. 서버가 title 을 넣어 보냈으면(data-vmid) 원래 제목을 알 수 없어 빈 문자열 */
function readDefaultTitle() {
    return document.head.querySelector("title[data-vmid]") ? "" : document.title;
}

/** 처음 열린 문서에 정적으로 들어 있는 meta(name/property). 서버가 넣은 태그(data-vmid)는 뺀다 */
function readStaticMetaTags() {
    return [...document.head.querySelectorAll("meta[name]:not([data-vmid]):not([data-vue-meta]), meta[property]:not([data-vmid]):not([data-vue-meta])")].map((element) => ({
        element,
        tag: { type: "meta", data: [...element.attributes].map(({ name, value }) => ({ key: name, value })) },
    }));
}

export default {
    metaInfo(){
        return this.metatag;
    },
    data(){
        return {
            timer: null,
            latest: Date.now(),

            metatag: readServerMetaInfo(),
            metatagTitle: readDefaultTitle(),
            metatagLoaded: false,
            metatagPath: null,
            metatagStatics: null,
        };
    },
    created(){
        this.init();
        // 첫 이동이 끝나기 전 $route 는 START("/") 라서, 첫 이동이 끝난 뒤부터 주소를 본다
        this.$router.onReady(() => {
            this.$watch("$route.path", this.onRoutePath, { immediate: true });
        });
    },
    methods: {
        init(){
            document.addEventListener("mousemove", this.reset, false);
            document.addEventListener("mousedown", this.reset, false);
            document.addEventListener("keypress", this.reset, false);
            document.addEventListener("touchmove", this.reset, false);
            document.addEventListener("onscroll", this.reset, false);

            this.onTimer();
        },
        reset(){
            this.latest = Date.now();
        },
        onRoutePath(path){
            if (!path || path.startsWith("/console")) return;
            this.loadMetatag(path);
        },
        // 페이지 이동을 막지 않도록 실패하면 지금 상태(처음에는 서버가 넣은 태그와 제목)를 둔다
        async loadMetatag(path){
            this.metatagPath = path;
            if (!this.metatagStatics) this.metatagStatics = readStaticMetaTags();
            try {
                const { metatag, variables_object } = await api.v1.metatags.get({ params: { path } });
                if (this.metatagPath != path) return;

                // 정적 meta 는 처음 한 번 vue-meta 관리로 넘긴다 (지우고 기본값으로 다시 넣는다)
                if (!this.metatagLoaded) this.metatagStatics.forEach(({ element }) => element.remove());
                this.metatagLoaded = true;
                const tags = (metatag?.tags || []).map(({ type, data }) => ({ type, data: data.map(({ key, value }) => ({ key, value: interpolateCommonMetatag_Value(value || "", variables_object) })) }));
                this.metatag = toMetaInfo(tags, this.metatagStatics.map(({ tag }) => tag), this.metatagTitle);
            } catch (error) {
                console.error(error);
            }
        },
        onTimer(){
            clearTimeout();
            this.timer = setTimeout(() => {
                if(24 * 60 * 60 * 1000 < (Date.now() - this.latest) || this.payload.exp * 1000 <= Date.now()){
                    this.$store.dispatch("logout");
                }
                this.onTimer();
            }, 1000);
        },
    },
    computed: {
        refreshToken(){
            return this.$store.state.refreshToken;
        },
        accessToken(){
            return this.$store.state.accessToken;
        },
        payload(){
            return JSON.parse(atob(this.refreshToken?.split('.')[1] || '') || "{}");
        }
    },
    watch: {
        accessToken(){
            if(!this.accessToken){
                alert("로그아웃 되었습니다");
                this.$router.push("/");
            }
        }
    }
}
</script>